import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { brand } from '../theme';
import { gsap, useGsap, QUERIES, splitHeading } from '../animation/gsap';

/**
 * A section title that assembles itself, character by character, as it arrives.
 *
 * The eyebrow fades in, the title's characters rise into place on a stagger,
 * and the gold bar draws out underneath.
 *
 * With reduced motion switched on, none of that runs and the heading is just a
 * heading — which is also what a visitor sees if this file never loads, because
 * nothing is hidden until GSAP decides to hide it.
 */
export default function SectionHeading({
	eyebrow,
	title,
	lead,
	align = 'left',
	invert = false,
	sx = {},
}) {
	const root = useGsap(
		({ mm }) => {
			// Wide screens: the per-character entrance, the gold bar drawing in.
			mm.add(QUERIES.desktop, () => {
				const heading = root.current.querySelector('[data-split]');
				const eyebrowEl = root.current.querySelector('[data-eyebrow]');
				const leadEl = root.current.querySelector('[data-lead]');
				const bar = root.current.querySelector('[data-bar]');

				if (!heading) {
					return undefined;
				}

				const { split, revert } = splitHeading(heading);

				const timeline = gsap.timeline({
					scrollTrigger: { trigger: root.current, start: 'top 80%' },
				});

				if (eyebrowEl) {
					timeline.from(eyebrowEl, { autoAlpha: 0, y: 12, duration: 0.5, ease: 'power2.out' });
				}

				timeline.from(
					split.chars,
					{
						autoAlpha: 0,
						yPercent: 110,
						rotateZ: 4,
						duration: 0.7,
						ease: 'power3.out',
						stagger: 0.014,
					},
					eyebrowEl ? '-=0.25' : 0
				);

				if (bar) {
					timeline.from(bar, { scaleX: 0, duration: 0.6, ease: 'power2.out' }, '-=0.35');
				}

				if (leadEl) {
					timeline.from(leadEl, { autoAlpha: 0, y: 16, duration: 0.6, ease: 'power2.out' }, '-=0.4');
				}

				// SplitText rewrites the DOM, so it has to be put back when this
				// section unmounts or the motion preference changes.
				return () => revert();
			});

			// Phones: whole blocks, no splitting. Character spans wrap mid-word on
			// narrow screens, and that is worse than any entrance is good.
			mm.add(QUERIES.mobile, () => {
				const parts = ['[data-eyebrow]', '[data-split]', '[data-bar]', '[data-lead]']
					.map((selector) => root.current.querySelector(selector))
					.filter(Boolean);

				gsap.from(parts, {
					autoAlpha: 0,
					y: 24,
					duration: 0.6,
					stagger: 0.1,
					ease: 'power2.out',
					scrollTrigger: { trigger: root.current, start: 'top 80%' },
				});
			});
		},
		[title, eyebrow, lead]
	);

	return (
		<Box
			ref={root}
			sx={{
				textAlign: align,
				maxWidth: align === 'center' ? '46rem' : 'none',
				marginInline: align === 'center' ? 'auto' : 0,
				...sx,
			}}
		>
			{eyebrow ? (
				<Typography data-eyebrow variant="overline" sx={{ display: 'block', color: brand.accent, mb: 1.5 }}>
					{eyebrow}
				</Typography>
			) : null}

			<Typography
				data-split
				variant="h2"
				component="h2"
				sx={{ color: invert ? brand.white : brand.text }}
			>
				{title}
			</Typography>

			<Box
				data-bar
				sx={{
					width: '3.5rem',
					height: '3px',
					mt: 2.5,
					borderRadius: '2px',
					backgroundColor: brand.accent,
					marginInline: align === 'center' ? 'auto' : 0,
					transformOrigin: align === 'center' ? 'center' : 'left',
				}}
			/>

			{lead ? (
				<Typography
					data-lead
					sx={{
						mt: 2.5,
						maxWidth: '52ch',
						marginInline: align === 'center' ? 'auto' : 0,
						color: invert ? brand.textInvertMuted : brand.textMuted,
						fontSize: '1.05rem',
					}}
				>
					{lead}
				</Typography>
			) : null}
		</Box>
	);
}
